/**
 * `<WidgetSnapshotWriter />` (`D280`): keeps the home-screen widgets'
 * snapshot in step with what Today shows. Mounted by the shell beside
 * `<WorkoutRuntime />`, and ONLY where the shell carries the `Widgets`
 * plugin — so the web and an older APK never make the three reads for it.
 *
 * It reads the queries Today already reads (today's session, `GET /me/stats`,
 * `GET /me/recovery`), from the same cache, and writes only when the
 * serialised snapshot changed: a re-render, a refetch with the same answer or
 * a second mount writes nothing. Offline it writes from the persisted cache;
 * a read that has nothing leaves its card out rather than inventing one.
 *
 * Renders nothing.
 */
import { useEffect } from "react";
import { writeWidgetSnapshot } from "../../native";
import { appI18n } from "../auth/i18n";
import { sessionName } from "../plan/session-name";
import { useRecovery } from "../progress/use-progress-data";
import { setsIn } from "../today/model";
import { useStats } from "../today/use-today";
import {
  type TodaysSessionState,
  useTodaysSession,
} from "../workout/screens/use-workout-data";
import { useWorkoutSession } from "../workout/store/selectors";
import { buildWidgetSnapshot, type TodayFact } from "./snapshot";

/** The last snapshot written in this process, so an equal one is skipped. */
let lastWritten: string | null = null;

/** Today's workout as the widget says it; `undefined` while not known. */
export function todayFactOf(
  state: TodaysSessionState,
  running: boolean
): TodayFact | undefined {
  if (state.status === "loading" || state.status === "error") {
    return undefined;
  }
  if (state.status === "none") {
    return state.plan === null ? { kind: "no-plan" } : { kind: "rest" };
  }
  const title = sessionName(state.session.label, state.session.name);
  if (running) {
    return { kind: "live", title };
  }
  return {
    exercises: state.session.items.length,
    kind: "planned",
    sets: setsIn(state.session),
    title,
  };
}

export function WidgetSnapshotWriter(): null {
  // Subscribed so a language change re-renders: every string in the
  // snapshot is final, and the serialised value below moves with them.
  appI18n.useLocale();
  const today = useTodaysSession();
  const stats = useStats().data;
  const recovery = useRecovery().data;
  const live = useWorkoutSession();
  const fact = todayFactOf(today, live !== null && live.ended === null);
  // Built in render and compared as a string: cheap, and the only
  // dependency the write needs — the date and the words are inside it.
  const snapshot = JSON.stringify(
    buildWidgetSnapshot({
      now: new Date(),
      ...(fact === undefined ? {} : { today: fact }),
      ...(stats === undefined ? {} : { stats }),
      ...(recovery === undefined ? {} : { recovery }),
    })
  );

  // Unmounted (a sign-out, which also clears the snapshot): the next mount
  // writes whatever it has, even a snapshot equal to the last one.
  useEffect(
    () => () => {
      lastWritten = null;
    },
    []
  );

  useEffect(() => {
    if (snapshot === lastWritten) {
      return;
    }
    lastWritten = snapshot;
    writeWidgetSnapshot(snapshot).catch(() => undefined);
  }, [snapshot]);

  return null;
}
